import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import type {
  GetOfferV1Response,
  GetQuestionsKind,
} from '@backmarket/nuxt-layer-buyback/app/scopes/offer/api/types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { sleep } from '@backmarket/utils/async/sleep'

import type { FunnelForm } from './useFunnelQuestions'

interface UseFetchOfferOptions {
  kind: GetQuestionsKind
  minDelay?: number
  onBeforeFetch?: (formPayload: FunnelForm) => void
  onSuccess?: (
    offer: GetOfferV1Response,
    formPayload: FunnelForm,
  ) => Promise<void> | void
  onNoOffer?: (formPayload: FunnelForm) => Promise<void> | void
  onError?: (error: HttpApiError, formPayload: FunnelForm) => void
}

// useAsyncData is not used here intentionally: the offer fetch triggers complex
// branching side effects (navigation, tracking, toasts) depending on the result.
// These callbacks don't map cleanly to useAsyncData's transform/error model,
// so isLoading is managed manually and callbacks are dispatched after the fetch.
export function useFetchOffer({
  kind,
  minDelay,
  onBeforeFetch,
  onSuccess,
  onNoOffer,
  onError,
}: UseFetchOfferOptions) {
  const $httpFetch = useHttpFetch()
  const isLoading = ref(false)

  const experiments = useExperiments()

  const isPrecisionGradingEnabled = computed(
    () => experiments['experiment.precisionGradingV2'] === 'with',
  )

  async function fetchOffer(formPayload: FunnelForm): Promise<void> {
    isLoading.value = true
    onBeforeFetch?.(formPayload)

    let offerResult: GetOfferV1Response | null = null
    let offerError: HttpApiError | null = null
    let isNoOffer = false

    try {
      const offerPromise = $httpFetch(
        '/buyback-funnel/api/v1/funnel/:kind/offer',
        {
          query: {
            ...formPayload,
            precisionGrading: String(!!isPrecisionGradingEnabled.value),
          },
          pathParams: { kind },
        },
      )

      const [offer] = await Promise.all([
        offerPromise,
        ...(minDelay ? [sleep(minDelay)] : []),
      ])

      offerResult = offer
    } catch (error) {
      const httpError = HttpApiError.fromAnyError(error)

      if (httpError.status === 404) {
        isNoOffer = true
      } else {
        offerError = httpError
      }
    }

    try {
      if (offerResult) {
        await onSuccess?.(offerResult, formPayload)
      } else if (isNoOffer) {
        await onNoOffer?.(formPayload)
      } else if (offerError) {
        onError?.(offerError, formPayload)
      }
    } finally {
      isLoading.value = false
    }
  }

  return { fetchOffer, isLoading }
}
